import { Injectable, OnInit } from "@angular/core";
import { Apollo } from "apollo-angular";
import { Firestore, collection, collectionData, query, where } from '@angular/fire/firestore';

import { BehaviorSubject, Observable } from "rxjs";
import gql from "graphql-tag";

import { LocalStorageService } from "./local-storage.service";
import { environment } from "src/environments/environment";
import { LOCAL_STORAGE } from "../constants/local-storage.constants";
import {
	BusinessInput,
	PlatformType,
	Resource,
} from "../model/businessInput.model";
import { LayoutService } from "./layout.service";
import { PaymentGatewaysService } from "./payment-gateways.service";
import { map } from "rxjs/operators";
import { BusinessRegion } from "../enums/business-type.enum";
import { SessionStorageService } from "./session-storage.service";
import { SESSION_STORAGE } from "../constants/session-storage.constants";

export const GET_URLS_IMAGE_SLIDER = gql`
	query GetUrlsImageSliders($id: String!) {
		GetUrlsImageSliders(id: $id)
	}
`;
export const BUSINESS_DETAIL_QUERY = gql`
	query getBusinessById($businessId: String!, $merchantId: String!) {
		GetBusinessById(businessId: $businessId, merchantId: $merchantId) {
			id
			name
			merchantId
			isEnabled
			address {
				line1
				line2
				state
				city
				country
				zip
				timeZone
				coordinates {
					latitude
					longitude
				}
			}
			vertical
			digitalAddresses {
				address
				preferred
				addressType
			}
			businessAttributes {
				taxes {
					name
					rate
				}
				theme
				catalogues
				servicePlatforms {
					type
					businessTimings
					slots
					isEnabled
					platformFees
					liveCatalogueId
					avgServiceTimeInMin
					deliveryFeesAbsorptionPercentage
					vendor {
						name
					}
					resources {
						id
						name
						attributes
						isEnabled
						serviceCapacity
						serviceTimeInMin
					}
				}
			}
		}
	}
`;

export const ALL_LIVE_BUSINESSES_QUERY = gql`
	query GetAllBusinessByLocation($city: String!) {
		GetAllBusinessByLocation(city: $city) {
			id
			name
			merchantId
			isEnabled
			address {
				line1
				line2
				state
				city
				country
				zip
				timeZone
				coordinates {
					latitude
					longitude
				}
			}
			vertical
			digitalAddresses {
				address
				preferred
				addressType
			}
			businessAttributes {
				taxes {
					name
					rate
				}
				theme
				catalogues
				servicePlatforms {
					type
					businessTimings
					slots
					isEnabled
					platformFees
					liveCatalogueId
					avgServiceTimeInMin
					deliveryFeesAbsorptionPercentage
					vendor {
						name
					}
					resources {
						id
						name
						attributes
						isEnabled
						serviceCapacity
						serviceTimeInMin
					}
				}
			}
		}
	}
`;

export const GET_PLATFORM_FEES = gql`
	query GetPlatformFees($businessId: String!, $merchantId: String!) {
		GetPlatformFees(businessId: $businessId, merchantId: $merchantId)
	}
`;

export const GET_BUSINESS_BY_NAME = gql`
	query GetBusinessByName($shortName: String!) {
		GetBusinessByName(shortName: $shortName) {
			id
			name
			merchantId
			isEnabled
			address {
				line1
				line2
				state
				city
				country
				zip
				timeZone
			}
			vertical
			digitalAddresses {
				address
				preferred
				addressType
			}
			businessAttributes {
				taxes {
					name
					rate
				}
				theme
				catalogues
				servicePlatforms {
					type
					businessTimings
					slots
					isEnabled
					platformFees
					liveCatalogueId
					avgServiceTimeInMin
					resources {
						id
						name
						attributes
						isEnabled
						serviceCapacity
						serviceTimeInMin
					}
				}
			}
		}
	}
`;

export const GET_BUSINESS_AND_UTM_BY_SHORT_NAME_AND_UTM_ID = gql`
	query GetBusinessAndUTMByShortNameandUtmId($shortName: String!, $utmId: String!) {
		GetBusinessAndUTMByShortNameandUtmId(shortName: $shortName, utmId: $utmId) {
	        busienss {
			id
			name
			merchantId
			isEnabled
			address {
				line1
				line2
				state
				city
				country
				zip
				timeZone
				coordinates {
					latitude
					longitude
				}
			}
			vertical
			digitalAddresses {
				address
				preferred
				addressType
			}
			businessAttributes {
				taxes {
					name
					rate
				}
				theme
				catalogues
				servicePlatforms {
					type
					businessTimings
					slots
					isEnabled
					platformFees
					liveCatalogueId
					avgServiceTimeInMin
					deliveryFeesAbsorptionPercentage
					vendor {
						name
					}
					resources {
						id
						name
						attributes
						isEnabled
						serviceCapacity
						serviceTimeInMin
					}
				}
			}
		}
		utmParams {
				id
				utmId
				campaignName
				content
				source
				medium
				terms
			}
		}
	}
`;

@Injectable({
	providedIn: "root",
})
export class BusinessService {
	private business: BehaviorSubject<BusinessInput | null>;

	constructor(
		public apollo: Apollo,
		private af: Firestore,
		private layoutService: LayoutService,
		private paymentGatewayService: PaymentGatewaysService,
	) {
		this.business = new BehaviorSubject<BusinessInput | null>(null);
	}

	//Get Image Urls from firestore for banner
	getUrlsImageSlider() {
		return this.apollo.watchQuery({
			query: GET_URLS_IMAGE_SLIDER,
			variables: {
				id: SessionStorageService.getValue(SESSION_STORAGE.BUSINESSID),
			},
		});
	}

	getBusinessDetail() {
		return this.apollo
			.watchQuery({
				query: BUSINESS_DETAIL_QUERY,
				variables: {
					businessId: SessionStorageService.getValue(SESSION_STORAGE.BUSINESSID),
					merchantId: SessionStorageService.getValue(SESSION_STORAGE.MERCHANT_ID),
				},
			})
			.valueChanges.subscribe((result: any) => {
				const business = result?.data?.GetBusinessById;
				if (!business) {
					return;
				}

				this.paymentGatewayService.getPaymentGateway();
				this.business.next(business);

				SessionStorageService.setValue(
					SESSION_STORAGE.VERTICAL,
					business.vertical?.toUpperCase?.() ?? '',
				);
				this.setApplicationTheme(
					this.business.value?.businessAttributes?.theme ?? '',
				);
			});
	}

	getBusinessById(businessId: string, merchantId: string) {
		return this.apollo.watchQuery({
			query: BUSINESS_DETAIL_QUERY,
			variables: {
				businessId: businessId,
				merchantId: merchantId,
			},
		}).valueChanges;
	}

	getBusinessAndUTMByShortNameandUtmId(shortName: string, utmId: string) {
		console.log(
			"BusinessService.getBusinessAndUTMByShortNameandUtmId called with:",
			{ shortName, utmId }
		);
		return this.apollo.watchQuery({
			query: GET_BUSINESS_AND_UTM_BY_SHORT_NAME_AND_UTM_ID,
			variables: {
				shortName: shortName,
				utmId: utmId,
			},
		}).valueChanges;
	}

	setBusiness(business: BusinessInput) {
		this.business.next(business);
		this.setBusinessLocally(business);
		this.setApplicationTheme(business.businessAttributes.theme);
	}

	getBusinessSubscription() {
		if (!this.business.value) {
			this.getBusinessDetail();
		}

		return this.business.asObservable();
	}

	getBusinessByNameSubscription(shortName:any) {
		if (!this.business.value) {
			this.getBusinessByName(shortName);
		}
		return this.business.asObservable();
	}

	getBusinessByName(shortName: string) {
		return this.apollo
			.watchQuery({
				query: GET_BUSINESS_BY_NAME,
				variables: {
					shortName: shortName,
				},
			})
			.valueChanges.subscribe((result: any) => {
				if (result.data.GetBusinessByName) {
					this.business.next(result.data.GetBusinessByName);
					this.setApplicationTheme(
						this.business.value?.businessAttributes?.theme ?? '',
					);
					this.setBusinessLocally(result.data.GetBusinessByName);
				}
			});
	}

	setBusinessLocally(business: BusinessInput) {
		SessionStorageService.setValue(
			SESSION_STORAGE.BUSINESS_DETAILS,
			JSON.stringify(business),
		);
	}

	getPlatformFees() {
		return this.apollo.watchQuery({
			query: GET_PLATFORM_FEES,
			variables: {
				businessId: SessionStorageService.getValue(SESSION_STORAGE.BUSINESSID),
				merchantId: SessionStorageService.getValue(SESSION_STORAGE.MERCHANT_ID),
			},
		});
	}

	setApplicationTheme(theme: string) {
		console.log("theme", theme);
		LocalStorageService.setValue(LOCAL_STORAGE.THEME, theme);
		this.layoutService.addBodyClass(theme);
	}

	getBusinessValue() {
		return this.business.value;
	}

	getAreasByBusinessId(businessId: string): Observable<string[]> {
		const deliveryAreasQuery = query(
			collection(this.af, 'BusinessDeliveryAreas'),
			where('businessId', '==', businessId)
		);

		return collectionData(deliveryAreasQuery).pipe(
			map((docs: any[]) => docs.length > 0 ? docs[0].areas : [])
		);
		// return this.af.collection('BusinessDeliveryAreas', ref =>
		// 	ref.where('businessId', '==', businessId))
		// 	.valueChanges()
		// 	.pipe(
		// 		map((docs: any[]) => docs.length > 0 ? docs[0].areas : [])
		// 	);
	}

	getTaxType() {
		let businessInput: BusinessInput;
		let taxType: string;
		this.getBusinessSubscription().subscribe((business) => {
			if (business) {
				businessInput = business;
			} else {
				businessInput = JSON.parse(
					SessionStorageService.getValue(SESSION_STORAGE.BUSINESS_DETAILS) ?? '{}'
				);
			}

			switch (businessInput.address.country) {
				case BusinessRegion.INDIA:
					taxType = "GST";
					break;
				case BusinessRegion.US:
					taxType = "Items tax";
					break;

				default:
					return "Items tax";
			}
		});

		// return taxType ? taxType : "Items tax";
	}

	getAllLiveBusinesses(city: string) {
		return this.apollo.query({
			query: ALL_LIVE_BUSINESSES_QUERY,
			variables: {
				city: city,
			},
			fetchPolicy: "network-only",
		});
		// .valueChanges;
		// .valueChanges.pipe(
		// 	map((result: any) => result?.data?.GetAllBusinessByLocation ?? [])
		// );
	}
}
